/**
 * The governed transfer-instruction screens' route table (C4 §6.5).
 *
 * These live beside `web/src/modules/cash/routes.ts` rather than inside it
 * because `routes.ts` is `internal/cash`'s v1 file and this milestone owns only
 * the `transfers/` subtree. The router spreads both.
 *
 * **`/cash/transfers` (the v1 immediate path) is untouched.** A company running
 * `transfer_mode = 'immediate'` keeps posting both legs in one call from that
 * screen; a company on `governed` gets a 422 there naming these paths instead.
 * Two screens, because they are two artefacts: an immediate transfer is a
 * posting and an instruction is a document with a lifecycle, and blurring them
 * is how a user believes they have paid when they have only drafted.
 *
 * **The title keys are `treasury.*`, not `cash.*`.** All of C4's strings live in
 * one fragment, `web/src/modules/treasury/locales.*.json`, because C4 owns
 * `web/src/modules/treasury/**` outright while `web/src/modules/cash/locales.*.json`
 * is v1's file and this packet owns only `cash/transfers/**`. The merge script
 * reads fragments one directory deep and requires the top-level key to equal the
 * directory name, so a nested `cash/transfers/locales.en.json` would never be
 * merged at all.
 */

import type { RouteRecordRaw } from 'vue-router'

import { CASH_C4_PERMISSIONS } from '@/modules/cash/transfers/permissions'

export const CASH_TRANSFER_ROUTES: RouteRecordRaw[] = [
  {
    path: '/cash/transfer-instructions',
    name: 'cash-transfer-instructions',
    component: () => import('@/modules/cash/transfers/InstructionsListView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.instructions',
      permission: CASH_C4_PERMISSIONS.transferRead,
    },
  },
  {
    path: '/cash/funds-in-transit',
    name: 'cash-funds-in-transit',
    component: () => import('@/modules/cash/transfers/FundsInTransitView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.fundsInTransit',
      permission: CASH_C4_PERMISSIONS.transferRead,
    },
  },
  {
    path: '/cash/transfer-instructions/:id(\\d+)',
    name: 'cash-transfer-instruction',
    component: () => import('@/modules/cash/transfers/InstructionView.vue'),
    meta: {
      titleKey: 'treasury.routeTitles.instruction',
      permission: CASH_C4_PERMISSIONS.transferRead,
    },
  },
]

export const CASH_TRANSFER_PATHS = {
  instructions: '/cash/transfer-instructions',
  instruction: (id: number) => `/cash/transfer-instructions/${id}`,
  fundsInTransit: '/cash/funds-in-transit',
} as const

/**
 * The in-transit lane filtered to one ageing band, from the aging report.
 *
 * The query key is `band` — the server's own word, and what the lane reads. It
 * was `bucket`, which nothing on the receiving screen looked at, so every link
 * from a band landed on an unfiltered lane.
 */
export function transitBandRoute(band: string): string {
  return `${CASH_TRANSFER_PATHS.fundsInTransit}?band=${encodeURIComponent(band)}`
}
